import React from "react";
import { SubmissionError } from "redux-form";
import _ from "underscore";

import { RouterLink } from "@js/components/link";
import { openModalAndWaitForInput } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import type {
  EmployerOwnJob,
  Job,
  NodeStaffMainJobListingJob,
  UpdateJobFeedbackData,
} from "@js/types/jobs";
import { assertUnreachable, getFormSubmitError } from "@js/utils";

import {
  useCompleteJobMutation,
  useUpdateJobFeedbackMutation,
} from "../../api";
import type { CloseJobFormData } from "../../forms/close-job";
import {
  CloseFilledJobForm,
  CloseJobForm,
  UNSURE_CLIENT_UNREACHABLE,
} from "../../forms/close-job";
import { CloseNotFilledJobForm } from "../../forms/close-job/close-not-filled-job-form";

const STEP = {
  CLOSE_JOB_FORM_MODAL_OPENED: "CLOSE_JOB_FORM_MODAL_OPENED",
  NOT_FILLED_JOB_MODAL_OPENED: "NOT_FILLED_JOB_MODAL_OPENED",
  FILLED_JOB_MODAL_OPENED: "FILLED_JOB_MODAL_OPENED",
} as const;

type StepType = EnumType<typeof STEP>;

const useCloseJob = ({
  job,
  onSuccess,
}: {
  job: Job | EmployerOwnJob | NodeStaffMainJobListingJob;
  onSuccess: () => void;
}) => {
  const [completeJob, { isSuccess: hasCompletedJob }] = useCompleteJobMutation({
    fixedCacheKey: `complete-job-${job.id}-mutation`,
  });
  const [step, setStepObject] = React.useState<{
    current: StepType;
    prev: StepType | null;
  }>({
    current: STEP.CLOSE_JOB_FORM_MODAL_OPENED,
    prev: null,
  });
  const [updateJobFeedback] = useUpdateJobFeedbackMutation();

  const setStep = (newStep: StepType) => {
    setStepObject({
      current: newStep,
      prev: step.current,
    });
  };

  const jobId = job.id;

  const _onSuccess = () => {
    onSuccess();
    openSuccessToast();
  };

  const sendJobFeedback = async (clientFeedback: UpdateJobFeedbackData) => {
    if (!_.isEmpty(clientFeedback)) {
      const notNullData = Object.fromEntries(
        Object.entries(clientFeedback).filter(([, value]) => value !== null),
      );

      return await updateJobFeedback({
        jobId,
        data: notNullData as UpdateJobFeedbackData,
      }).unwrap();
    }
  };

  const handleFirstStepSubmit = async (values: CloseJobFormData) => {
    if (values.client_feedback_was_job_filled === UNSURE_CLIENT_UNREACHABLE) {
      try {
        await openModalAndWaitForInput(
          { children: <>Are you sure you want to close this job?</> },
          { confirmText: "Close job", cancelText: "Cancel" },
        );
        await completeJob({
          jobId,
          data: { client_feedback_was_job_filled: null },
        }).unwrap();
        _onSuccess();
      } catch (_error) {
        // user cancelled
      }
    } else if (values.client_feedback_was_job_filled) {
      setStep(STEP.FILLED_JOB_MODAL_OPENED);
    } else {
      setStep(STEP.NOT_FILLED_JOB_MODAL_OPENED);
    }
  };

  const handleFeedbackSubmit =
    (wasJobFilled: boolean) =>
    async (clientFeedback: UpdateJobFeedbackData) => {
      try {
        if (!hasCompletedJob) {
          await completeJob({
            jobId,
            data: {
              client_feedback_was_job_filled: wasJobFilled,
            },
          }).unwrap();
        }
      } catch (err: unknown) {
        const error = getFormSubmitError(err);
        Snackbar.error("Job completion failed!");
        throw new SubmissionError(error?.data as object);
      }
      try {
        await sendJobFeedback(clientFeedback);
        _onSuccess();
      } catch (err: unknown) {
        const error = getFormSubmitError(err);
        Snackbar.error("Sending job feedback failed!");
        throw new SubmissionError(error?.data as object);
      }
    };

  const prevStep = step.prev;
  const currentStep = step.current;
  const firstStepInitialValues = React.useMemo(() => {
    const userCameBackFromDifferentStep = prevStep !== null;
    const userCameBackFromFilledStep =
      userCameBackFromDifferentStep &&
      prevStep === STEP.FILLED_JOB_MODAL_OPENED;
    return {
      client_feedback_was_job_filled: userCameBackFromDifferentStep
        ? userCameBackFromFilledStep
        : null,
    };
  }, [prevStep]);

  return {
    handleFirstStepSubmit,
    handleFeedbackSubmit,
    currentStep,
    setStep,
    firstStepInitialValues,
  };
};

export const CloseJobModalManager = ({
  modalInstance,
  afterAction,
  job,
}: {
  modalInstance: { close: () => void };
  afterAction: (job: Job | EmployerOwnJob | NodeStaffMainJobListingJob) => void;
  job: Job | EmployerOwnJob | NodeStaffMainJobListingJob;
}) => {
  const {
    handleFirstStepSubmit,
    handleFeedbackSubmit,
    currentStep,
    setStep,
    firstStepInitialValues,
  } = useCloseJob({
    job,
    onSuccess: () => {
      afterAction(job);
      modalInstance.close();
    },
  });

  switch (currentStep) {
    case STEP.CLOSE_JOB_FORM_MODAL_OPENED: {
      return (
        <CloseJobForm
          onSubmit={handleFirstStepSubmit}
          initialValues={firstStepInitialValues}
          onCancel={modalInstance.close}
        />
      );
    }
    case STEP.FILLED_JOB_MODAL_OPENED: {
      return (
        <CloseFilledJobForm
          onSubmit={handleFeedbackSubmit(true)}
          onCancelClick={() => {
            setStep(STEP.CLOSE_JOB_FORM_MODAL_OPENED);
          }}
        />
      );
    }
    case STEP.NOT_FILLED_JOB_MODAL_OPENED: {
      return (
        <CloseNotFilledJobForm
          onSubmit={handleFeedbackSubmit(false)}
          onCancelClick={() => {
            setStep(STEP.CLOSE_JOB_FORM_MODAL_OPENED);
          }}
        />
      );
    }
    default: {
      assertUnreachable(currentStep);
      return null;
    }
  }
};

export const openSuccessToast = () =>
  Snackbar.toast({
    header: "This job is now closed.",
    content: (closeSnackbar) => {
      return (
        <>
          It has been removed from the Braintrust Talent job search. If you need
          to post this job again, head over to the{" "}
          <RouterLink
            onClick={() => {
              closeSnackbar();
            }}
            style={{ textDecoration: "underline" }}
            to="/jobs/add_new"
          >
            Post Job
          </RouterLink>{" "}
          page and create a new posting.
        </>
      );
    },
  });
